import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.error || t('errors.generic'));
      }
    } catch (err) {
      setError(t('forgotPassword.networkError'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-4 py-8">
        <div className="max-w-md w-full my-auto">
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border-2 border-gray-200">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('forgotPassword.checkEmail')}</h2>
            <p className="text-gray-600 mb-6">
              {t('forgotPassword.emailSent', { email })}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {t('forgotPassword.linkExpires')}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-gray-900 hover:text-gray-700 font-medium underline"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('forgotPassword.backToLogin')}
            </Link>
          </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 py-8 overflow-y-auto">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border-2 border-gray-200">
          <div className="text-center mb-6 sm:mb-8">
            <img src="/tahc-logo-2026.jpg" alt="tahc" className="w-24 h-24 sm:w-40 sm:h-40 mx-auto mb-4 sm:mb-6 object-contain" />
            <p className="text-gray-600 text-sm sm:text-base">
              {t('forgotPassword.instruction')}
            </p>
          </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              {t('forgotPassword.emailAddress')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-gray-900 focus:border-transparent transition"
                placeholder={t('forgotPassword.emailPlaceholder')}
                required
                autoComplete="email"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white py-3 px-4 rounded-full hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? t('forgotPassword.sending') : t('forgotPassword.sendResetLink')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-gray-900 hover:text-gray-700 text-sm font-medium underline"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('forgotPassword.backToLogin')}
          </Link>
        </div>
        </div>
      </div>
    </div>
  );
}
